<script lang="ts">
  import { ActivityTag } from '@/Common/ActivityTag';
  import TagWrapper from '@/Common/TagWrapper.svelte';
  import { cn } from '@/lib/utils';
  import { convertSeconds, formatXSSDetails, handleImageError, kmToMile } from '@/Util/utils';
  import DifficultyDiamonds from '@/DifficultyDiamonds.svelte';
  import { Activity, type Exercise } from '@/Common/ActivityFunctions';
  import XSSDisplay from '@/Common/XSSDisplay.svelte';
  import SuitabilityBadges from './SuitabilityBadges.svelte';
  import { onMount } from 'svelte';

  export let exercise: Exercise;
  export let size: 'sm' | 'lg' = 'lg';

  //@ts-ignore
  const Xert = window.Xert;
  let useFreedomUnits: boolean = Xert._displayUnits == 'imperial';

  // @ts-ignore
  const jq = window.jQuery;

  function tidy(number) {
    return Math.round(10 * Math.abs(number)) / 10;
  }

  const activityTag = ActivityTag('small');
  let activityTags = [
    activityTag.exerciseType({ ...exercise, exerciseType: exercise.exerciseType ?? '' }),
  ].filter((tag) => tag);

  // Derived display values
  $: distanceVal = tidy(useFreedomUnits ? kmToMile(exercise.distance ?? 0) : (exercise.distance ?? 0));
  $: distanceUnit = useFreedomUnits ? 'mi' : 'km';
  $: durationStr = convertSeconds(exercise.duration ?? exercise.adu, false);
  $: difficulty = exercise.difficulty ?? null;
  $: focusOrType = exercise.focus ?? exercise.type ?? '';
  $: diamondsLabel = difficulty ? `Difficulty ${difficulty}` : '';
  $: avgPower = Math.round(exercise.avg_power) ?? null;
  $: maxPower = Math.round(exercise.max_power) ?? null;

  onMount(() => {
    jq('[data-toggle=tooltip]').tooltip({container: '#shadcn-ui-dialog-target'});
  });

</script>

<div class={cn('flex flex-col gap-1', size == 'lg' ? 'w-72' : 'w-48', 'text-sm')}>
  <div class={cn("flex items-center", size == 'lg' ? '' : 'h-[40px]')}>
    <h3 class={cn("!whitespace-normal w-full font-medium m-0", size == 'lg' ? 'text-base line-clamp-1' : 'text-sm line-clamp-2')}>
      {#if Activity.getDetailsPageLink(exercise)}
        <a class='text-foreground hover:underline' href={Activity.getDetailsPageLink(exercise)} target={Activity.isWorkout(exercise) ? "workout" : "activity"}>
          {exercise.name}
        </a>
      {:else}
        {exercise.name}
      {/if}
    </h3>
  </div>
  <div class={cn("flex flex-row gap-1", size == 'lg' ? 'text-sm' : 'text-xs')}>
    <SuitabilityBadges suitability={exercise.suitability?.split(',').map(tag => tag.trim()) ?? []} isLiked={exercise.liked || exercise.favorite} isDisliked={exercise.disliked} />
  </div>
  <img class='rounded-md border border-border' src={Activity.getOneImage(exercise)?.url ?? Activity.getBackupImageUrl()} alt="Chart" draggable="false" on:error={handleImageError} />
  <div class={cn('mt-1 flex flex-col gap-2', size==='lg' ? 'text-sm' : 'text-xs')}>
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <span class="inline-flex items-center gap-1 text-gray-700"><i class="fa-regular fa-clock text-[11px] text-gray-500" aria-hidden="true"></i>{durationStr}</span>
        {#if distanceVal > 0}
          <span class="inline-flex items-center gap-1 text-gray-700"><i class="fa-regular fa-road text-[11px] text-gray-500" aria-hidden="true"></i>{distanceVal} {distanceUnit}</span>
        {:else}
          {#if avgPower}
            <span data-toggle="tooltip" title='Average Power' class="inline-flex items-center gap-1 text-gray-700"><i class="fa-regular fa-gauge text-[11px] text-gray-500" aria-hidden="true"></i>{avgPower} W</span>
          {/if}
          {#if maxPower && size == 'lg'}
            <span data-toggle="tooltip" title='Max Power' class="inline-flex items-center gap-1 text-gray-700"><i class="fa-regular fa-gauge-max text-[11px] text-gray-500" aria-hidden="true"></i>{maxPower} W</span>
          {/if}
        {/if}
      </div>
      <TagWrapper tags={activityTags} />
    </div>

    <div class="flex items-center gap-2 w-full min-w-0 overflow-hidden">
      <div class="flex items-center gap-3 flex-1 min-w-0">
      {#if difficulty}
        <div class="flex items-center gap-1 shrink-0" aria-label={diamondsLabel} title={diamondsLabel}>
        <DifficultyDiamonds difficulty={difficulty} compact={true} />
        </div>
      {/if}
      {#if size == 'lg' || !difficulty}
        <div class="flex items-center gap-1 flex-1 basis-0 min-w-0 overflow-hidden" aria-label="Focus / Type">
          <span class="truncate block max-w-full">{focusOrType}</span>
        </div>
      {/if}
      </div>
      <div class="flex items-center gap-1 shrink-0" aria-label="XSS Load">
      <XSSDisplay
        className={cn('font-semibold', size === 'lg' ? 'text-sm' : 'text-xs')}
        xss={formatXSSDetails({
          xss: exercise.xss ?? 0,
          xlss: exercise.xlss ?? 0,
          xhss: exercise.xhss ?? 0,
          xpss: exercise.xpss ?? 0,
        })}
      />
      </div>
    </div>
  </div>
</div>
